import React, { FC } from "react";
import { t, Trans } from "@helpers/translate";
import { GetStaticProps } from "next";
import { graphql } from "@helpers/graphql";
import {
  AuthorType,
  PostCategoriesQuery,
  PostOrderBy,
  PostsQuery,
} from "@graphql/types";
import Link from "next/link";
import { getNodes } from "@helpers/getNodes";
import { CACHE_TIME_MORE } from "@constants";
import Head from "@components/common/Head";
import { Title } from "@components/ui/Title";
import { Paragraph } from "@components/ui/Paragraph";
import BlogSearch from "@components/common/BlogSearch";
import { Container } from "@components/ui/Container";
import PostsBlock from "@components/common/PostsBlock";
import { ButtonLink } from "@components/ui/Button";
import PostAllCategoriesBlock from "@components/common/PostAllCategoriesBlock";
import ScrollableMenu from "@components/ui/ScrollableMenu/ScrollableMenu";
import { ROUTES } from "@root/routes";
import ScrollToTop from "@components/ui/ScrollToTop/ScrollToTop";

const POST_POPULAR_VARIABLES = {
  first: 8,
  filters: {
    AUTHOR: [AuthorType.Company],
    ORDER_BY: PostOrderBy.CreatedAtDesc,
  },
};

const POST_GUIDES_POPULAR_VARIABLES = {
  first: 8,
  filters: {
    AUTHOR: [AuthorType.Supplier],
    ORDER_BY: PostOrderBy.CreatedAtDesc,
  },
};

export const getPostOrderBy = (orderBy: string) => {
  if (orderBy === "latest") {
    return PostOrderBy.Latest;
  }
  if (orderBy === "createdAtAsc") {
    return PostOrderBy.CreatedAtAsc;
  }
  if (orderBy === "createdAtDesc") {
    return PostOrderBy.CreatedAtDesc;
  }
  return PostOrderBy.Best;
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      initialData: {
        companyPosts: await graphql.posts(POST_POPULAR_VARIABLES),
        guidesPosts: await graphql.posts(POST_GUIDES_POPULAR_VARIABLES),
        categories: await graphql.postCategories({
          filters: { ORDER_BY_MOST_POSTS_COUNT: true },
        }),
      },
    },
    revalidate: CACHE_TIME_MORE, //  5 hours cache
  };
};

type Props = {
  initialData: {
    companyPosts: PostsQuery;
    guidesPosts: PostsQuery;
    categories: PostCategoriesQuery;
  };
};

const Page: FC<Props> = ({ initialData }) => {
  const categories = initialData.categories.postCategories.edges.map(
    (item) => ({
      name: item.node.name,
      slug: item.node.slug,
      countPosts: item.node.countPosts,
    })
  );

  return (
    <>
      <Head
        title={t({
          id: "blog.seo.title.new1",
          message: `Blog | Travel Blog`,
        })}
        description=""
      />
      <div className="py-20">
        <div className="mb-20">
          <Title className="text-center">
            <Trans id="blog.h1">Blog</Trans>
          </Title>
          <Paragraph centered>
            <Trans id="blog.h2">
              Learn from our local travel experts and guides
            </Trans>
          </Paragraph>
          <div className="max-md:mx-2">
            <BlogSearch />
          </div>
        </div>

        {/* For mobile screen */}
        <div className=" block sm:hidden bg-gwg_category_bg my-2 shadow-lg py-4 w-full">
          <div className="px-1 mx-auto max-w-6xl">
            <div className="text-center w-screen">
              <h2 className="text-xl lg:mr-10 mr-5">
                <Trans id="blog.category">Category</Trans>
              </h2>
            </div>

            {/* Filter and display categories */}
            <ScrollableMenu
              items={categories.filter((c) => c.countPosts > 0)}
            />
          </div>
        </div>

        {/* For desktop screen */}
        <div className=" hidden sm:flex bg-gwg_category_bg my-2 shadow-lg py-4 w-full">
          <div className="flex px-4 mx-auto max-w-6xl">
            <div className="flex flex-col items-center w-screen">
              <h2 className="text-xl lg:mr-10 mr-5">
                <Trans id="blog.category">Category</Trans>
              </h2>

              <div className="flex w-full py-4 md:flex-wrap md:space-x-2 md:space-y-2 overflow-x-auto">
                <div></div>
                {categories
                  .filter((c) => c.countPosts > 0)
                  .map((item, key) => (
                    <div
                      key={key}
                      className="whitespace-nowrap h-8 w-auto border border-gray-200 bg-white text-gwg_dull_gray rounded-2xl px-3 py-2 text-xs"
                    >
                      <Link
                        href={ROUTES.blog.category(item.slug)}
                        title={t({
                          id: "common.link.title",
                          message:
                            "GoWithGuide - Private Tours & Local Tour Guides",
                        })}
                        className="hover:underline text-gray-600"
                      >
                        {item.name}
                      </Link>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        <Container className="py-14 pr-0 md:pr-8">
          <PostsBlock
            orientation="vertical"
            title={t({
              id: "blog.block.gowithguide-posts.title",
              message: "GoWithGuide Posts",
            })}
            data={getNodes(initialData.companyPosts.posts)}
            showUser={true}
            columns="4"
            className="mb-8"
            enableSwipeOnMobile={true}
          >
            <ButtonLink
              href={ROUTES.blog.company()}
              variant="default-outline"
              title={t({
                id: "blog.block.supplier-posts.view.gowithguide.posts",
                message: "GoWithGuide - View All GoWithGuide Posts",
              })}
            >
              <Trans id="blog.block.gowithguide-posts.button">
                View all GoWithGuide&apos;s Posts
              </Trans>
            </ButtonLink>
          </PostsBlock>

          <PostsBlock
            orientation="vertical"
            title={t({
              id: "blog.block.supplier-posts.title",
              message: "Guide's Posts",
            })}
            columns="4"
            data={getNodes(initialData.guidesPosts.posts)}
            className="mb-8"
            enableSwipeOnMobile={true}
          >
            <ButtonLink
              href={ROUTES.blog.guides()}
              variant="default-outline"
              title={t({
                id: "blog.block.supplier-posts.view.guides.posts",
                message: "GoWithGuide - View All Guide Posts",
              })}
            >
              <Trans id="blog.block.supplier-posts.button">
                View all Guide&apos;s Posts
              </Trans>
            </ButtonLink>
          </PostsBlock>

          {/* Categories Block */}
          <PostAllCategoriesBlock
            data={initialData.categories}
            title={t({
              id: "blog.block.categories.title",
              message: "Categories",
            })}
          />

          <ScrollToTop />
        </Container>
      </div>
    </>
  );
};

export default Page;
